import { useState } from "react";
import { Plus, Pencil, Trash2, Layers, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, LoadingState, ErrorState, EmptyState, Field, FormError, SubmitButton, ConfirmDialog, Badge, StatusBadge } from "@/components/common";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { t } from "@/i18n";

const FLAGS = ["customer_portal", "online_booking", "quotes_and_invoices", "messaging", "photo_storage", "follow_ups", "referral", "inventory"];
const EMPTY = { name: "", description: "", max_internal_users: 1, photo_quota_mb: 500, features: {}, price_monthly: "", price_yearly: "", trial_days: 14, card_required: false, public: false, active: true };
const num = (v) => (v === "" || v == null ? null : Number(v));

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span>{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
    </label>
  );
}

function PlanDialog({ plan, open, onOpenChange, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lastPlan, setLastPlan] = useState(null);
  if (open && plan !== lastPlan) {
    setLastPlan(plan);
    setF(plan ? { ...EMPTY, ...plan, price_monthly: plan.price_monthly ?? "", price_yearly: plan.price_yearly ?? "" } : EMPTY);
    setError("");
  }
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = { name: f.name, description: f.description, features: f.features, card_required: f.card_required, public: f.public, active: f.active,
      max_internal_users: Number(f.max_internal_users), photo_quota_mb: Number(f.photo_quota_mb), trial_days: Number(f.trial_days),
      price_monthly: num(f.price_monthly), price_yearly: num(f.price_yearly), currency: "AUD" };
    try {
      if (plan) await api.put(`/admin/plans/${plan.id}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success(t("plans.saved"));
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setLastPlan(null); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="plan-dialog">
        <DialogHeader>
          <DialogTitle>{plan ? t("plans.edit") : t("plans.create")}</DialogTitle>
          <DialogDescription>{t("plans.subtitle")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          <Field label={t("plans.name")} htmlFor="pl-name"><Input id="pl-name" required value={f.name} onChange={set("name")} data-testid="plan-name-input" /></Field>
          <Field label={t("plans.description")} htmlFor="pl-desc"><Textarea id="pl-desc" value={f.description} onChange={set("description")} data-testid="plan-description-input" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("p7.plans.priceMonthly")} htmlFor="pl-pm" hint={t("p7.plans.priceBlank")}><Input id="pl-pm" type="number" min={0} step="0.01" value={f.price_monthly} onChange={set("price_monthly")} data-testid="plan-price-monthly-input" /></Field>
            <Field label={t("p7.plans.priceYearly")} htmlFor="pl-py" hint={t("p7.plans.priceBlank")}><Input id="pl-py" type="number" min={0} step="0.01" value={f.price_yearly} onChange={set("price_yearly")} data-testid="plan-price-yearly-input" /></Field>
            <Field label={t("p7.plans.trialDays")} htmlFor="pl-trial"><Input id="pl-trial" type="number" min={0} max={365} required value={f.trial_days} onChange={set("trial_days")} data-testid="plan-trial-days-input" /></Field>
            <Field label={t("plans.maxUsers")} htmlFor="pl-max"><Input id="pl-max" type="number" min={1} required value={f.max_internal_users} onChange={set("max_internal_users")} data-testid="plan-max-users-input" /></Field>
            <Field label={t("plans.quota")} htmlFor="pl-quota"><Input id="pl-quota" type="number" min={0} required value={f.photo_quota_mb} onChange={set("photo_quota_mb")} data-testid="plan-quota-input" /></Field>
          </div>
          <div className="space-y-2 rounded-lg border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-800">{t("plans.features")}</p>
            {FLAGS.map((k) => <Toggle key={k} label={t(`plans.flag.${k}`)} checked={f.features[k]} onChange={(v) => setF({ ...f, features: { ...f.features, [k]: v } })} testId={`plan-flag-${k}`} />)}
          </div>
          <div className="space-y-2 rounded-lg border border-slate-200 p-4">
            <Toggle label={t("p7.plans.cardRequired")} checked={f.card_required} onChange={(v) => setF({ ...f, card_required: v })} testId="plan-card-required-switch" />
            <Toggle label={t("p7.plans.public")} checked={f.public} onChange={(v) => setF({ ...f, public: v })} testId="plan-public-switch" />
            <Toggle label={t("plans.activeLabel")} checked={f.active} onChange={(v) => setF({ ...f, active: v })} testId="plan-active-switch" />
          </div>
          <div className="gf-modal-footer">
            <SubmitButton busy={busy} className="w-full" data-testid="plan-save-button">{t("common.save")}</SubmitButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PlanCard({ p, onEdit, onDelete, onDeactivate }) {
  const priced = p.price_monthly || p.price_yearly;
  return (
    <div className="gf-card flex flex-col p-6" data-testid={`plan-card-${p.name.toLowerCase()}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-display text-2xl font-extrabold text-slate-900">{p.name}</p>
          <p className="mt-1 text-sm text-slate-600">{p.description}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={p.active ? "active" : "inactive"} />
          {p.public && <Badge tone="teal" testId="plan-public-badge">{t("p7.plans.publicBadge")}</Badge>}
        </div>
      </div>
      <dl className="mt-5 space-y-2 text-sm">
        <div className="flex justify-between"><dt className="text-slate-500">{t("p7.plans.priceMonthly")}</dt><dd className="font-semibold" data-testid="plan-price-monthly">{p.price_monthly ? fmtMoney(p.price_monthly) : "—"}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">{t("p7.plans.priceYearly")}</dt><dd className="font-semibold" data-testid="plan-price-yearly">{p.price_yearly ? fmtMoney(p.price_yearly) : "—"}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">{t("p7.plans.trialDays")}</dt><dd className="font-semibold">{p.trial_days ?? 14}{p.card_required ? " · card" : ""}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">{t("plans.maxUsers")}</dt><dd className="font-semibold">{p.max_internal_users}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">{t("plans.quota")}</dt><dd className="font-semibold">{p.photo_quota_mb} MB</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">{t("plans.garages")}</dt><dd className="font-semibold">{p.garages_count}</dd></div>
      </dl>
      {!priced && <p className="mt-3 text-xs font-semibold text-amber-700" data-testid="plan-no-price">{t("p7.plans.noPrice")}</p>}
      {p.sample_prices && <p className="mt-3 text-xs font-semibold text-amber-700" data-testid="plan-sample-price">{t("p7.plans.sample")}</p>}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {FLAGS.filter((k) => p.features?.[k]).map((k) => <Badge key={k} tone="teal">{t(`plans.flag.${k}`)}</Badge>)}
      </div>
      <div className="mt-auto flex gap-2 pt-6">
        <Button variant="outline" size="sm" onClick={onEdit} data-testid={`edit-plan-${p.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> {t("common.edit")}</Button>
        {p.garages_count > 0
          ? p.active && <Button variant="ghost" size="sm" className="text-amber-700 hover:bg-amber-50" onClick={onDeactivate} data-testid={`deactivate-plan-${p.id}`}><Trash2 className="mr-1.5 h-3.5 w-3.5" /> {t("p7.plans.deactivate")}</Button>
          : <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={onDelete} data-testid={`delete-plan-${p.id}`}><Trash2 className="mr-1.5 h-3.5 w-3.5" /> {t("common.delete")}</Button>}
      </div>
    </div>
  );
}

export default function AdminPlans() {
  const { data, error, loading, reload } = useFetch("/admin/plans");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const deactivate = async (p) => {
    try {
      const { id, garages_count, created_at, updated_at, is_demo, sample_prices, stripe_product_id, ...rest } = p; // eslint-disable-line no-unused-vars
      await api.put(`/admin/plans/${id}`, { ...rest, active: false, public: false });
      toast.success(t("p7.plans.deactivated"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/plans/${del.id}`);
      toast.success(t("plans.deleted"));
      setDel(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow={t("admin.area")} title={t("admin.nav.plans")} subtitle={t("plans.subtitle")}
        actions={<Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="create-plan-button"><Plus className="mr-2 h-4 w-4" /> {t("plans.create")}</Button>} />
      {data?.some((p) => p.sample_prices) && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="sample-prices-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span><strong>{t("p7.plans.sampleTitle")}</strong> {t("p7.plans.sampleText")}</span>
        </div>
      )}
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.length === 0 && <EmptyState icon={Layers} title={t("plans.empty")} />}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {data?.map((p) => <PlanCard key={p.id} p={p} onEdit={() => { setEditing(p); setOpen(true); }} onDelete={() => setDel(p)} onDeactivate={() => deactivate(p)} />)}
      </div>
      <PlanDialog plan={editing} open={open} onOpenChange={setOpen} onSaved={() => { setOpen(false); reload(); }} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} busy={busy} destructive title={t("plans.deleteTitle")} description={t("plans.deleteText")} confirmLabel={t("common.delete")} onConfirm={remove} testId="delete-plan-dialog" />
    </div>
  );
}
